import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';

import Context from '../../../utils/context';
import { getConversation } from '../../../api/conversations';
import { redirectAfterSurgery } from '../../../helpers/redirect_after_surgery_helper.js';
import PostConversationModal from '../../questionnaire/post_conversation_survey_modal/index_gated.jsx';
import menuStyles from '../../chrome/admin_header/user_menu.module.css';
import sidebarStyles from '../../users/conversation_room/unified_layout.module.css';

const LAST_CONVERSATION_STORAGE_KEY = 'super_user_patient_last_conversation_uuid';

export function AdminBackIcon() {
  return (
    <svg width='18' height='18' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
      <path d='M14 6L8 12L14 18' stroke='currentColor' strokeWidth='1.9' strokeLinecap='round' strokeLinejoin='round' />
      <path d='M19 12H8' stroke='currentColor' strokeWidth='1.9' strokeLinecap='round' />
    </svg>
  );
}

export function AdminConversationsIcon() {
  return (
    <svg
      width='18'
      height='18'
      viewBox='0 0 256 256'
      fill='currentColor'
      xmlns='http://www.w3.org/2000/svg'
      aria-hidden='true'
    >
      <path d='M232,128A104,104,0,0,1,79.12,219.82L45.07,231.17a16,16,0,0,1-20.24-20.24l11.35-34.05A104,104,0,1,1,232,128Z' />
    </svg>
  );
}

export function AdminSurveyIcon() {
  return (
    <svg width='18' height='18' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
      <path
        d='M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15'
        stroke='currentColor'
        strokeWidth='1.7'
        strokeLinecap='round'
      />
      <path
        d='M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5V7H9V5Z'
        stroke='currentColor'
        strokeWidth='1.7'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path d='M9 12H15' stroke='currentColor' strokeWidth='1.7' strokeLinecap='round' />
      <path d='M9 16H13' stroke='currentColor' strokeWidth='1.7' strokeLinecap='round' />
    </svg>
  );
}

export function usePatientSessionNav(conversationProp) {
  const context = useContext(Context);
  const history = useHistory();
  const conversationMatch = useRouteMatch('/member/conversations/:conversation_id');
  const user = context.auth.getCurrentUser();
  const isSuperUser = context.auth.isSuperUser && context.auth.isSuperUser();
  const isPatientSession = user?.current_profile_type === 'Patient';
  const canShow = isSuperUser;
  const mentorMatch = user?.current_profile?.mentor_match;
  const [fetchedConversation, setFetchedConversation] = useState(null);
  const conversation = conversationProp || fetchedConversation;

  useEffect(() => {
    if (!canShow || conversationProp) return undefined;
    const conversationId = conversationMatch?.params?.conversation_id;
    if (!conversationId) {
      setFetchedConversation(null);
      return undefined;
    }

    getConversation(
      conversationId,
      (data) => setFetchedConversation(data),
      () => setFetchedConversation(null)
    );

    return undefined;
  }, [canShow, conversationProp, conversationMatch?.params?.conversation_id]);

  const postSurveyPath =
    conversation?.post_survey_path && conversation.post_survey_path !== 'not_applicable'
      ? conversation.post_survey_path
      : null;

  const mentorFirstName = conversation?.patient_mentor_name?.trim()?.split(/\s+/)?.[0] || null;

  const conversationUuid = conversation?.uuid || conversationMatch?.params?.conversation_id || null;

  useEffect(() => {
    if (!canShow || !conversationUuid) return undefined;
    window.sessionStorage.setItem(LAST_CONVERSATION_STORAGE_KEY, conversationUuid);
    return undefined;
  }, [canShow, conversationUuid]);

  const rememberConversation = () => {
    if (conversationUuid) {
      window.sessionStorage.setItem(LAST_CONVERSATION_STORAGE_KEY, conversationUuid);
    }
  };

  const resolveConversationsPath = () => {
    const storedUuid = window.sessionStorage.getItem(LAST_CONVERSATION_STORAGE_KEY);
    const targetUuid = conversationUuid || storedUuid;
    if (targetUuid) return `/member/conversations/${targetUuid}`;
    return '/member/conversations';
  };

  return {
    canShow,
    questionnaireDisabled: !isPatientSession || !mentorMatch?.uuid || !mentorMatch?.account_slug,
    postSurveyDisabled: !postSurveyPath,
    postSurveyPath,
    mentorFirstName,
    goToQuestionnaire: () => {
      rememberConversation();
      redirectAfterSurgery(history, mentorMatch, user?.account);
    },
    openPostSurvey: () => {
      if (!postSurveyPath) return;
      rememberConversation();
    },
    goToConversations: () => {
      history.push(resolveConversationsPath());
    },
  };
}

export default function PatientSessionNavButtons({
  variant = 'sidebar',
  conversation,
  onNavigate,
  onOpenPostSurvey,
  menuActionClassName,
  menuActionIconClassName,
}) {
  const nav = usePatientSessionNav(conversation);
  const styles = variant === 'menu' ? menuStyles : sidebarStyles;
  const [showPostSurveyModal, setShowPostSurveyModal] = useState(false);

  if (!nav.canShow) return null;

  const buttonClassName =
    variant === 'menu'
      ? menuActionClassName || `${styles.item} ${styles.adminButton} ${styles.adminButtonInfo}`
      : `${styles.app_sidebar_item} ${styles.app_sidebar_admin_button} ${styles.app_sidebar_admin_button_info}`;
  const iconClassName = variant === 'menu' ? menuActionIconClassName || styles.itemIcon : styles.app_sidebar_icon;

  const handleQuestionnaire = () => {
    nav.goToQuestionnaire();
    if (typeof onNavigate === 'function') onNavigate();
  };

  const handlePostSurvey = () => {
    nav.openPostSurvey();
    if (typeof onOpenPostSurvey === 'function') {
      onOpenPostSurvey({ postSurveyPath: nav.postSurveyPath, mentorFirstName: nav.mentorFirstName });
    } else {
      setShowPostSurveyModal(true);
    }
    if (typeof onNavigate === 'function') onNavigate();
  };

  const handleConversations = () => {
    nav.goToConversations();
    if (typeof onNavigate === 'function') onNavigate();
  };

  return (
    <>
      <button type='button' className={buttonClassName} onClick={handleConversations} title='Go to conversations'>
        <span className={iconClassName}>
          <AdminConversationsIcon />
        </span>
        <span>Go to convos</span>
      </button>

      <button
        type='button'
        className={buttonClassName}
        onClick={handleQuestionnaire}
        disabled={nav.questionnaireDisabled}
        title='Go to patient signup questionnaire (mentor match flow)'
      >
        <span className={iconClassName}>
          <AdminBackIcon />
        </span>
        <span>Go to signup</span>
      </button>

      <button
        type='button'
        className={buttonClassName}
        onClick={handlePostSurvey}
        disabled={nav.postSurveyDisabled}
        title='Go to post-conversation survey'
      >
        <span className={iconClassName}>
          <AdminSurveyIcon />
        </span>
        <span>Go to survey</span>
      </button>

      {!onOpenPostSurvey && showPostSurveyModal && nav.postSurveyPath && (
        <PostConversationModal
          postConversationPath={nav.postSurveyPath}
          mentorName={nav.mentorFirstName}
          onDismiss={() => setShowPostSurveyModal(false)}
          onClose={() => setShowPostSurveyModal(false)}
        />
      )}
    </>
  );
}
